import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Receipt as ReceiptIcon } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState, Section } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function Expenses() {
  const { t, expCats, money } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [filterCat, setFilterCat] = useState("all");
  const [form, setForm] = useState({ category: expCats[0], amount: "", date: today, paid_by: "", payment_method: "cash", description: "" });

  const load = async () => {
    try {
      const r = await api.get("/expenses");
      setList(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filterCat === "all" ? list : list.filter((e) => e.category === filterCat);
  const totalAmt = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error(t("error")); return; }
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (edit) await api.put(`/expenses/${edit.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(t("success"));
      setOpen(false); setEdit(null);
      setForm({ category: expCats[0], amount: "", date: today, paid_by: "", payment_method: "cash", description: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("expenses")}</h1>
          <p className="text-sm text-gray-500 mt-1 tabular">{t("total")}: {money(totalAmt)}</p>
        </div>
        <button
          data-testid="add-expense-btn"
          onClick={() => { setEdit(null); setForm({ category: expCats[0], amount: "", date: today, paid_by: "", payment_method: "cash", description: "" }); setOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]"
        >
          <Plus className="w-4 h-4" /> {t("addExpense")}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilterCat("all")} className={`px-3 py-1.5 rounded-full text-sm border ${filterCat === "all" ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}>{t("allCategories")}</button>
        {expCats.map((c) => (
          <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-1.5 rounded-full text-sm border ${filterCat === c ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}>{c}</button>
        ))}
      </div>

      <Section title={t("expenses")}>
        {filtered.length === 0 ? <EmptyState icon={ReceiptIcon} title={t("empty")} /> : (
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-right text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr>
                  <th className="p-3">{t("date")}</th>
                  <th className="p-3">{t("category")}</th>
                  <th className="p-3">{t("description")}</th>
                  <th className="p-3">{t("paidBy")}</th>
                  <th className="p-3">{t("amount")}</th>
                  <th className="p-3">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100">
                    <td className="p-3 text-gray-700 tabular">{e.date}</td>
                    <td className="p-3 text-gray-900 font-medium">{e.category}</td>
                    <td className="p-3 text-gray-500">{e.description || "-"}</td>
                    <td className="p-3 text-gray-500">{e.paid_by || "-"}</td>
                    <td className="p-3 tabular font-bold text-rose-600">{money(e.amount)}</td>
                    <td className="p-3 flex gap-2">
                      <button onClick={() => { setEdit(e); setForm({ category: e.category, amount: e.amount, date: e.date, paid_by: e.paid_by, payment_method: e.payment_method, description: e.description }); setOpen(true); }} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(e.id)} className="p-2 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader><DialogTitle>{edit ? t("edit") : t("addExpense")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <RpSelect
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
              options={expCats.map((c) => ({ value: c, label: c }))}
            />
            <input data-testid="exp-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={t("amount")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input value={form.paid_by} onChange={(e) => setForm({ ...form, paid_by: e.target.value })} placeholder={t("paidBy")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <RpSelect
              value={form.payment_method}
              onValueChange={(v) => setForm({ ...form, payment_method: v })}
              options={[
                { value: "cash", label: t("cash") },
                { value: "card", label: t("card") },
                { value: "bank", label: t("bank") },
                { value: "mobile", label: t("mobile") },
              ]}
            />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("description")} className="w-full p-3 rounded-xl border border-gray-200 min-h-[80px]" />
          </div>
          <DialogFooter className="flex-row-reverse">
            <button data-testid="save-expense" onClick={save} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("save")}</button>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaggerRoot>
  );
}
